import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { tandianApi, tshareUrl, bloggerEnrollUrl } from '../../services/api';
import { AdminHeader, GhostBtn, MerchantBtn, PrimaryBtn } from './AdminLayout';
import QrModal from './QrModal';
import { useLightbox } from '../../components/Lightbox';

// 把纯文本里的 http(s) 网址渲染成可点链接（小红书链接等）
function linkify(text) {
  if (!text) return text;
  return String(text).split(/(https?:\/\/[^\s]+)/g).map((p, i) =>
    /^https?:\/\//i.test(p)
      ? <a key={i} href={p} target="_blank" rel="noreferrer" style={{ color: '#FA1C51', textDecoration: 'underline', wordBreak: 'break-all' }}>{p}</a>
      : p
  );
}

const STATUS = {
  applied:         { label: '已报名', color: '#2563eb', bg: '#eff6ff' },
  listed:          { label: '已推商家·待选', color: '#b45309', bg: '#fffbeb' },
  selected:        { label: '商家已选中', color: '#7c3aed', bg: '#f5f3ff' },
  not_selected:    { label: '商家未选中', color: '#9ca3af', bg: '#f3f4f6' },
  visited:         { label: '已探店', color: '#0891b2', bg: '#ecfeff' },
  draft_submitted: { label: '待审草稿', color: '#b45309', bg: '#fffbeb' },
  draft_approved:  { label: '已过稿', color: '#15803d', bg: '#f0fdf4' },
  published:       { label: '已发布', color: '#FA1C51', bg: '#fff0f3' },
  done:            { label: '已完成', color: '#15803d', bg: '#f0fdf4' },
  cancelled:       { label: '已取消', color: '#9ca3af', bg: '#f3f4f6' },
};
const LIFECYCLE = [
  { key: 'recruiting', label: '报名 / 上架' },
  { key: 'selecting',  label: '商家选人' },
  { key: 'running',    label: '探店 / 确认草稿' },
  { key: 'completed',  label: '已完成' },
];
const STEP_HINT = {
  recruiting: '等博主报名，或点「补博主」；给合适的博主点「上架给商家」。',
  selecting:  '已有博主上架，把下面「① 选人」链接发给商家，让商家挑人。',
  running:    '商家已选人。安排探店、审核草稿，并用「② 确认草稿」让商家确认。',
  completed:  '全部完成。可用「③ 完整报告」把结果发给商家。',
};
const FILTERS = [
  { key: 'all', label: '全部', test: () => true },
  { key: 'applied', label: '待上架', test: (a) => a.status === 'applied' },
  { key: 'listed', label: '待选', test: (a) => a.status === 'listed' },
  { key: 'selected', label: '商家已选中', test: (a) => a.status === 'selected' },
  { key: 'needtime', label: '待确认时间', test: (a) => needsTimeConfirm(a) },
];
const CONSUMING = ['selected', 'visited', 'draft_submitted', 'draft_approved', 'published', 'done'];
const IN_PROGRESS = ['selected', 'visited', 'draft_submitted', 'draft_approved', 'published'];
// 时间/备注在哪些状态可改（失效的两种不给改）
const NOTE_EDITABLE = (s) => !['not_selected', 'cancelled'].includes(s);
// 改时间的统一提醒（运营端）
const TIME_TIP = '改完时间记得提醒博主：博主端「我的探店」会看到最新时间。';

// 阶段由数据自动推导，保证准确（不手动点）
function deriveStage(apps) {
  const chain = apps.filter(a => IN_PROGRESS.includes(a.status) || a.status === 'done');
  if (chain.length > 0) return chain.every(a => a.status === 'done') ? 'completed' : 'running';
  if (apps.some(a => a.status === 'listed')) return 'selecting';
  return 'recruiting';
}
// 当前该发给商家的哪一步
function merchantStepOf(stage) {
  if (stage === 'running') return 'drafts';
  if (stage === 'completed') return 'report';
  return 'select';
}

// 时间统一显示成 8月22日 周五 12:00（24小时制 + 星期，避免 am/pm 和星期几看错）
const WEEK = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
function hhmm(dt) { return `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`; }
// 复制文案里的时间跟界面显示保持一致
function fmtCopyTime(d) { return fmtVisit(d); }
function normalizeUrl(u) { if (!u) return ''; return /^https?:\/\//i.test(u) ? u : `https://${u}`; }
function toLocalInput(d) {
  const dt = new Date(d); if (isNaN(dt.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}T${p(dt.getHours())}:${p(dt.getMinutes())}`;
}
function fmtVisit(d) {
  const dt = new Date(d); if (isNaN(dt.getTime())) return '';
  return `${dt.getMonth() + 1}月${dt.getDate()}日 ${WEEK[dt.getDay()]} ${hhmm(dt)}`;
}
function groupOf(s) {
  if (IN_PROGRESS.includes(s)) return 1;
  if (s === 'done') return 2;
  if (s === 'listed') return 3;
  if (s === 'applied') return 4;
  return 5; // not_selected / cancelled
}
// 执行中按具体时间近→远排序（无时间排后）
function cmp(a, b) {
  const ga = groupOf(a.status), gb = groupOf(b.status);
  if (ga !== gb) return ga - gb;
  if (ga === 1) {
    const ta = a.visitAt ? new Date(a.visitAt).getTime() : Infinity;
    const tb = b.visitAt ? new Date(b.visitAt).getTime() : Infinity;
    if (ta !== tb) return ta - tb;
  }
  return new Date(a.createdAt) - new Date(b.createdAt);
}

// 复制到剪贴板（带降级）
function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  }
  return Promise.resolve(fallbackCopy(text));
}
function fallbackCopy(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand('copy'); document.body.removeChild(ta);
  } catch (e) { /* noop */ }
}
// 已选中且未定时间 —— 需要跟商家确认时间
function needsTimeConfirm(a) {
  return a.status === 'selected' && !a.visitAt && !String(a.visitTimeConfirmed || '').trim();
}
// 从博主填的自由文本里清洗出「时间」：有「时间：xxx」就取那段，否则整段当时间
function parseTimeFromRequested(text) {
  const s = String(text || '').trim();
  if (!s) return '';
  const m = s.match(/时间[：:]\s*([\s\S]*?)(?=\s*(?:人数|小红书|电话|姓名|店铺|链接)[：:]|$)/);
  return (m ? m[1] : s).trim().replace(/[，,、;；\s]+$/, '');
}
// 从自由文本里抓人数：优先「人数：N」，否则「N人」
function parsePeopleFromRequested(text) {
  const s = String(text || '');
  const m = s.match(/人数[：:]\s*(\d+)/) || s.match(/(\d+)\s*人(?!数)/);
  return m ? parseInt(m[1], 10) : 0;
}
// 最终人数：优先运营填的字段(>1)，否则从文本抓，否则 1
function confirmPeople(a) {
  if (a.visitPeopleCount && a.visitPeopleCount > 1) return a.visitPeopleCount;
  return parsePeopleFromRequested(a.visitTimeRequested) || a.visitPeopleCount || 1;
}
// 平台介绍：运营填的优先，空则回退用博主自己的简介
function platformBioOf(a) {
  return String(a.platformBio || '').trim() || String(a.bio || '');
}
const NUM_EMOJI = ['1\ufe0f\u20e3', '2\ufe0f\u20e3', '3\ufe0f\u20e3', '4\ufe0f\u20e3', '5\ufe0f\u20e3', '6\ufe0f\u20e3', '7\ufe0f\u20e3', '8\ufe0f\u20e3', '9\ufe0f\u20e3', '\ud83d\udd1f'];
function numPrefix(i) { return i < NUM_EMOJI.length ? NUM_EMOJI[i] : `${i + 1}.`; }
// 统一文案（发商家 / 发群里都用这个）：
// 1️⃣名字 电话 8.22号 12:00pm 2人 备注
// （空行）小红书：账号 / 主页链接 （空行）粉丝：N
// idx 为 null 时不带编号（单个博主复制）
function copyLine(a, idx) {
  const prefix = idx == null ? '' : numPrefix(idx);
  const time = a.visitAt ? fmtCopyTime(a.visitAt) : (parseTimeFromRequested(a.visitTimeRequested) || '时间待定');
  const head = [
    `${prefix}${a.bloggerName || '博主'}`,
    a.contactPhone || a.bloggerPhone || '',
    time,
    `${confirmPeople(a)}人`,
    String(a.note || '').trim(),
  ].filter(Boolean).join(' ');
  const lines = [head, '', `小红书：${a.xhsAccount || ''}`.trim()];
  if (a.xhsUrl) lines.push(normalizeUrl(a.xhsUrl));
  lines.push('', `粉丝：${a.xhsFollowers || 0}`);
  return lines.join('\n');
}
function copyTextOf(apps) { return apps.map((a, i) => copyLine(a, i)).join('\n\n'); }
// 发群里邀请博主的文案（配合入驻二维码 / 链接）
function inviteText(activity, url) {
  const lines = [`【${activity.merchantName || ''}】${activity.name} 探店招募`];
  if (activity.intro) lines.push('', activity.intro);
  lines.push(
    '',
    `报名链接：${url}`,
    '',
    '还没注册的博主，打开链接先注册并填资料，审核通过后在「可报名」里找到本活动报名。',
    '报名时请选准确的到店日期和时间；备注请参照报名要求填写，比如店铺和套餐，没有可以不填。',
  );
  return lines.join('\n');
}
// 博主基础信息：名字（粉丝）+ 小红书号/链接 + 其他平台 + 平台介绍（无电话、无报名信息）
function bloggerBaseText(a) {
  const lines = [`${a.bloggerName || '博主'}${a.xhsFollowers ? `（${a.xhsFollowers}粉）` : ''}`];
  if (a.xhsAccount || a.xhsUrl) lines.push(`小红书：${a.xhsAccount || ''}${a.xhsUrl ? ' ' + normalizeUrl(a.xhsUrl) : ''}`.trim());
  (a.otherPlatforms || []).forEach((p) => {
    if (!p || (!p.type && !p.account && !p.url)) return;
    const fans = p.followers ? `（${p.followers}粉）` : '';
    lines.push(`${p.type || '其他平台'}：${p.account || ''}${fans}${p.url ? ' ' + normalizeUrl(p.url) : ''}`.trim());
  });
  const pb = platformBioOf(a);
  if (pb) lines.push(`简介：${pb}`);
  return lines.join('\n');
}
export default function TandianActivityDetail() {
  const { activityId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewDraft, setViewDraft] = useState(null);
  const [adding, setAdding] = useState(false);
  const [approved, setApproved] = useState(null);
  const [shareModal, setShareModal] = useState(null); // { view, title, links? }
  const [editAct, setEditAct] = useState(false);
  const [showGuide, setShowGuide] = useState(false);   // 顶部说明/流程折叠
  const [sel, setSel] = useState(new Set());           // 多选的博主 appId
  const [openRows, setOpenRows] = useState(new Set()); // 展开的博主行 appId
  const [confirmModal, setConfirmModal] = useState(false); // 确认时间弹窗
  const [inviteModal, setInviteModal] = useState(false);   // 邀请博主（文案 + 入驻二维码）
  const [infoCard, setInfoCard] = useState(null);       // 博主信息卡
  const [editProfile, setEditProfile] = useState(null); // 编辑博主全部资料
  const [toast, setToast] = useState('');

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 1800); };

  const load = useCallback(() => {
    tandianApi.activity(activityId).then(setData).finally(() => setLoading(false));
  }, [activityId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (appId, body) => { await tandianApi.patchApp(appId, body); load(); };
  const batchPatch = async (ids, body) => {
    if (!ids.length) return;
    await Promise.all(ids.map((id) => tandianApi.patchApp(id, body)));
    setSel(new Set()); load();
  };
  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openAdd = async () => { setAdding(true); setApproved(null); setApproved(await tandianApi.bloggers({ status: 'approved' })); };
  const addBlogger = async (profileId) => {
    try { await tandianApi.addBlogger(activityId, profileId); setAdding(false); load(); }
    catch (e) { alert(e.response?.data?.error || '添加失败'); }
  };
  const del = async () => {
    if (!window.confirm('确认删除该活动？已占用的余额会退回商家。')) return;
    // 后端这条已经退役，回的是 410 + 一句人话（tandianRouter.ts）。
    // 没有 catch 的话运营点完删除界面一动不动 —— 失败了还不知道为什么，
    // 正是那边特意回 410 而不是 404 想避免的情况。同页 save/addBlogger 都是这么接的。
    try { await tandianApi.deleteActivity(activityId); navigate(-1); }
    catch (e) { alert(e.response?.data?.error || '删除失败'); }
  };

  if (loading || !data) {
    return <div style={st.page}><AdminHeader title="探店活动" back={true} /><div style={st.body}><p style={st.dim}>加载中…</p></div></div>;
  }
  const { activity, applications, merchant } = data;
  const merchantName = state?.merchantName || activity.merchantName;
  const listedCount = applications.filter(a => a.status === 'listed').length;
  const consuming = applications.filter(a => CONSUMING.includes(a.status));
  const published = applications.filter(a => ['published', 'done'].includes(a.status));
  const existingPhones = applications.map(a => a.bloggerPhone);
  const sorted = [...applications].sort(cmp);
  const draftLinks = applications
    .filter(a => a.status === 'draft_submitted')
    .map(a => ({ label: a.bloggerName || '博主', url: tshareUrl(activity.shareToken, { view: 'drafts', app: a._id }) }));
  const stage = deriveStage(applications);
  const stageIdx = LIFECYCLE.findIndex(x => x.key === stage);
  const pendingTime = applications.filter(needsTimeConfirm);
  let merchantStep = merchantStepOf(stage);
  if (stage === 'running' && pendingTime.length > 0) merchantStep = 'confirm-time';
  const MERCHANT_STEPS = [
    { view: 'select', label: '① 选人' },
    { view: 'confirm-time', label: '② 确认时间' },
    { view: 'drafts', label: '③ 确认草稿' },
    { view: 'report', label: '④ 完整报告' },
  ];
  const shown = sorted;
  const shownIds = shown.map(a => a._id);
  const selApps = applications.filter(a => sel.has(a._id));
  const selListable = selApps.filter(a => a.status === 'applied');
  const selPickable = selApps.filter(a => a.status === 'listed');
  const selDropable = selApps.filter(a => a.status === 'applied' || a.status === 'listed');
  const doCopy = () => copyText(copyTextOf(selApps)).then(() => showToast(`已复制 ${selApps.length} 位博主文案`));
  const listedText = copyTextOf(applications.filter(a => a.status === 'listed'));
  const openSelect = () => setShareModal({ view: 'select', title: '发给商家 · 选人', copyContent: listedText, copyContentLabel: '选人文案' });
  const chipClick = (ids) => setSel(prev => { const n = new Set(prev); const allIn = ids.length > 0 && ids.every(id => n.has(id)); ids.forEach(id => allIn ? n.delete(id) : n.add(id)); return n; });
  const allOpen = shown.length > 0 && shownIds.every(id => openRows.has(id));
  const toggleOpenAll = () => setOpenRows(prev => { const n = new Set(prev); shownIds.forEach(id => allOpen ? n.delete(id) : n.add(id)); return n; });
  const toggleRow = (id) => setOpenRows((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div style={st.page}>
      <AdminHeader title={activity.name} back={true} actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <MerchantBtn small onClick={openSelect}>商家链接</MerchantBtn>
          <GhostBtn small onClick={() => setEditAct(true)}>编辑</GhostBtn>
          <GhostBtn small onClick={del}>删除</GhostBtn>
        </div>
      } />
      <div style={st.body}>
        <div style={{ ...st.ovBar, cursor: 'pointer' }} onClick={() => setShowGuide(v => !v)}>
          <span style={st.ovBarMain}>{merchantName}</span>
          <span style={st.ovBarSub}>余额 <b style={{ color: '#FA1C51' }}>{merchant?.tandianBalance ?? '—'}</b> · 报名{applications.length}/上架{listedCount}/选中{consuming.length}/发布{published.length}</span>
          <span style={st.ovToggle}>{showGuide ? '收起说明 ▲' : '说明/流程 ▼'}</span>
        </div>

        {showGuide && (
          <div style={st.guideBox}>
            {activity.intro && <div style={st.ovIntro}>{activity.intro}</div>}
            <div style={st.stepper}>
              {LIFECYCLE.map((lc, i) => (
                <React.Fragment key={lc.key}>
                  {i > 0 && <div style={{ ...st.stepLine, ...(i <= stageIdx ? st.stepLineOn : {}) }} />}
                  <div style={{ ...st.stepPill, ...(i === stageIdx ? st.stepCur : i < stageIdx ? st.stepDone : st.stepTodo) }}>{lc.label}</div>
                </React.Fragment>
              ))}
            </div>
            <div style={st.stepHint}><b style={st.stepHintNow}>当前：{LIFECYCLE[stageIdx]?.label}</b> — {STEP_HINT[stage]}</div>
            <div style={st.mFlowHint}>①选人/③确认草稿/④报告是发给商家的链接或二维码；②确认时间是复制文案发微信。也可全程运营代操作，不需要商家开网页：勾选博主后底部可批量「上架给商家」「复制文案」「代商家选中」。「邀请博主」是发群里的招募文案＋报名二维码。</div>
          </div>
        )}

        <div style={st.mFlowBtns}>
          {MERCHANT_STEPS.map(step => {
            const active = step.view === merchantStep;
            const onClick = step.view === 'confirm-time'
              ? () => setConfirmModal(true)
              : step.view === 'select'
                ? openSelect
                : () => setShareModal({ view: step.view, title: '发给商家 · ' + step.label.replace(/^[①②③④]\s*/, ''), links: step.view === 'drafts' ? draftLinks : undefined });
            return (
              <button key={step.view} style={{ ...st.mStepBtn, ...(active ? st.mStepOn : st.mStepOff) }} onClick={onClick}>
                {step.label}{step.view === 'confirm-time' && pendingTime.length > 0 ? `(${pendingTime.length})` : ''}{active ? ' ·当前' : ''}
              </button>
            );
          })}
          <button style={st.inviteBtn} onClick={() => setInviteModal(true)}>邀请博主</button>
        </div>

        <div style={st.sectionTop}>
          <div style={st.chipWrap}>
            {applications.length > 0 && FILTERS.map(f => {
              const ids = applications.filter(f.test).map(a => a._id);
              const on = ids.length > 0 && ids.every(id => sel.has(id));
              return (
                <button key={f.key} disabled={ids.length === 0}
                  style={{ ...st.filterChip, ...(on ? st.filterChipOn : {}), ...(ids.length === 0 ? st.filterChipDim : {}) }}
                  onClick={() => chipClick(ids)}>
                  {f.label} {ids.length}
                </button>
              );
            })}
            {applications.length > 0 && <button style={st.selAllBtn} onClick={toggleOpenAll}>{allOpen ? '收起' : '展开'}</button>}
          </div>
          {stage !== 'completed' && <GhostBtn small onClick={openAdd}>+ 补博主</GhostBtn>}
        </div>

        {applications.length === 0 ? (
          <p style={st.dim}>还没有博主报名。「报名中」阶段博主可自助报名，或点「补博主」手动添加。</p>
        ) : (
          <div style={st.rowList}>
            {shown.map(a => (
              <BloggerRow key={a._id} a={a} patch={patch} setViewDraft={setViewDraft}
                selected={sel.has(a._id)} onToggleSel={() => toggleSel(a._id)} onCopyConfirm={showToast}
                open={openRows.has(a._id)} onToggleOpen={() => toggleRow(a._id)} onOpenInfo={() => setInfoCard(a)} />
            ))}
          </div>
        )}
        <div style={{ height: sel.size ? 76 : 24 }} />
      </div>

      {sel.size > 0 && (
        <div style={st.selBar}>
          <span style={st.selCount}>已选 {sel.size}</span>
          <button style={st.selCopy} onClick={doCopy}>复制文案</button>
          {selListable.length > 0 && <button style={st.selOk} onClick={() => batchPatch(selListable.map(a => a._id), { status: 'listed' })}>上架给商家({selListable.length})</button>}
          {selPickable.length > 0 && <button style={st.selOk} onClick={() => batchPatch(selPickable.map(a => a._id), { status: 'selected' })}>代商家选中({selPickable.length})</button>}
          {selDropable.length > 0 && <button style={st.selNo} onClick={() => batchPatch(selDropable.map(a => a._id), { status: 'not_selected' })}>不选({selDropable.length})</button>}
          <button style={st.selClear} onClick={() => setSel(new Set())}>清除</button>
        </div>
      )}

      {toast && <div style={st.toast}>{toast}</div>}

      {editAct && <EditActivityModal activity={activity} onSaved={() => { setEditAct(false); load(); }} onClose={() => setEditAct(false)} />}
      {viewDraft && <DraftModal app={viewDraft} patch={patch} onClose={() => setViewDraft(null)} onToast={showToast} />}
      {adding && <AddBloggerModal list={approved} existing={existingPhones} onPick={addBlogger} onClose={() => setAdding(false)} />}
      {confirmModal && <ConfirmTimeModal apps={applications} patch={patch} onClose={() => setConfirmModal(false)} onToast={showToast} />}
      {infoCard && <BloggerInfoCard a={infoCard} onClose={() => setInfoCard(null)} onToast={showToast} onSaved={load} onEditAll={() => { setEditProfile(infoCard); setInfoCard(null); }} />}
      {editProfile && <ProfileEditModal a={editProfile} patch={patch} onClose={() => setEditProfile(null)} onToast={showToast} onSaved={load} />}
      {inviteModal && (
        <QrModal
          url={bloggerEnrollUrl()}
          title="邀请博主 · 报名二维码"
          copyContent={inviteText(activity, bloggerEnrollUrl())}
          copyContentLabel="邀请文案"
          onClose={() => setInviteModal(false)}
        />
      )}
      {shareModal && (
        <QrModal
          url={tshareUrl(activity.shareToken, { view: shareModal.view })}
          previewUrl={tshareUrl(activity.shareToken, { view: shareModal.view })}
          title={shareModal.title}
          links={shareModal.links}
          copyContent={shareModal.copyContent}
          copyContentLabel={shareModal.copyContentLabel}
          accent="#7C3AED"
          onClose={() => setShareModal(null)}
        />
      )}
    </div>
  );
}

// 紧凑博主行：默认一行概览，点开展开操作区
function BloggerRow({ a, patch, setViewDraft, selected, onToggleSel, onCopyConfirm, open, onToggleOpen, onOpenInfo }) {
  const s = STATUS[a.status] || STATUS.applied;
  const pend = needsTimeConfirm(a);
  return (
    <div style={{ ...rw.card, ...(selected ? rw.cardSel : {}) }}>
      <div style={rw.main}>
        <input type="checkbox" checked={selected} onChange={onToggleSel} style={rw.cb} />
        <div style={rw.info} onClick={onToggleOpen}>
          <div style={rw.line1}>
            <span style={rw.name} onClick={(e) => { e.stopPropagation(); onOpenInfo(); }}>{a.bloggerName || '博主'}</span>
            {a.xhsAccount && <span style={rw.xhs}>· {a.xhsAccount}</span>}
            <span style={{ ...rw.badge, color: s.color, background: s.bg }}>{s.label}</span>
          </div>
          <div style={rw.line2}>
            {(a.xhsFollowers || 0)}粉 · {a.visitPeopleCount || 1}人 · {a.visitAt ? fmtVisit(a.visitAt) : (a.visitTimeRequested || '未填时间')}{a.note ? ` · ${a.note}` : ''}
          </div>
        </div>
        <button style={{ ...rw.copyBtn, ...(pend ? rw.copyBtnWarn : {}) }} onClick={() => copyText(copyLine(a, null)).then(() => onCopyConfirm('已复制文案'))}>复制文案</button>
        <button style={rw.editBtn} onClick={() => setViewDraft(a)}>编辑</button>
        <button style={rw.expand} onClick={onToggleOpen}>{open ? '收起' : '操作'}</button>
      </div>
      {open && (
        <div style={rw.detail}>
          <div style={st.reqRow}>
            <span style={st.reqTime}>探店时间：{a.visitAt ? fmtVisit(a.visitAt) : (a.visitTimeRequested ? linkify(a.visitTimeRequested) : '未填')} · {a.visitPeopleCount || 1}人</span>
            <button style={st.miniEdit} onClick={() => setViewDraft(a)}>编辑</button>
          </div>
          {a.note && <div style={st.noteLine}>备注：{linkify(a.note)}</div>}
          <div style={st.contactLine}>
            <span>姓名 {a.bloggerName || '未填'}</span>
            {a.preferredName ? <span> · 称呼 {a.preferredName}</span> : null}
            <span> · 电话 </span>
            {a.contactPhone ? <a style={st.contactTel} href={`tel:${a.contactPhone}`}>{a.contactPhone}</a> : <span>未填</span>}
          </div>
          {a.merchantComment && <div style={st.mComment}>商家意见：{a.merchantComment}</div>}
          <Actions a={a} patch={patch} setViewDraft={setViewDraft} />
        </div>
      )}
    </div>
  );
}

// 博主信息卡：点名字弹出，平台介绍可编辑 + 小红书链接跳转 + 复制基础信息 + 编辑全部资料
function BloggerInfoCard({ a, onClose, onToast, onSaved, onEditAll }) {
  const [pbio, setPbio] = useState(platformBioOf(a));
  const [saving, setSaving] = useState(false);
  const savePbio = async () => {
    if (!a.bloggerProfileId) { onToast && onToast('缺少博主档案，无法保存'); return; }
    setSaving(true);
    try { await tandianApi.updateBlogger(a.bloggerProfileId, { platformBio: pbio.trim() }); onSaved && onSaved(); onToast && onToast('已保存平台介绍'); }
    finally { setSaving(false); }
  };
  const copyBase = () => copyText(bloggerBaseText({ ...a, platformBio: pbio })).then(() => onToast && onToast('已复制基础信息'));
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>
        <div style={st.modalHead}>
          <p style={st.modalTitle}>博主信息</p>
          <button style={st.editBtn} onClick={onEditAll}>编辑全部资料</button>
        </div>
        <div style={st.modalScroll}>
          <div style={st.bcTop}>
            {a.avatar ? <img src={a.avatar} alt="" style={st.bcAvatar} /> : <div style={st.bcAvatarFallback}>{(a.bloggerName || '博')[0]}</div>}
            <div style={{ minWidth: 0 }}>
              <div style={st.bcName}>{a.bloggerName || '博主'}</div>
              <div style={st.bcFans}><b style={{ color: '#FA1C51' }}>{a.xhsFollowers || 0} 粉</b> · 小红书：{a.xhsAccount || '—'}</div>
            </div>
          </div>
          {a.xhsUrl && <a style={st.bcLink} href={normalizeUrl(a.xhsUrl)} target="_blank" rel="noreferrer">打开小红书主页 ›</a>}
          {(a.otherPlatforms || []).length > 0 && (
            <div style={st.bcPlats}>
              {a.otherPlatforms.map((p, i) => (
                <div key={i} style={st.bcPlatRow}>{p.type || '平台'}：{p.account || '—'}{p.followers ? `（${p.followers}粉）` : ''}{p.url && <a style={st.bcPlatLink} href={normalizeUrl(p.url)} target="_blank" rel="noreferrer">链接 ›</a>}</div>
              ))}
            </div>
          )}
          <div style={st.editLabel}>平台介绍（对外复制用；默认同博主简介，可改，改后不随博主简介变）</div>
          <textarea style={st.editArea} value={pbio} onChange={(e) => setPbio(e.target.value)} placeholder="平台介绍" />
          <button style={{ ...ac.ok, marginTop: 6 }} onClick={savePbio} disabled={saving}>{saving ? '保存中…' : '保存平台介绍'}</button>
        </div>
        <div style={st.modalBtns}>
          <button style={ac.act} onClick={copyBase}>复制基础信息</button>
          <GhostBtn onClick={onClose}>关闭</GhostBtn>
        </div>
      </div>
    </div>
  );
}

// 编辑博主全部资料（快捷入口，写入 BloggerProfile，并同步应用快照）
function ProfileEditModal({ a, patch, onClose, onToast, onSaved }) {
  const [f, setF] = useState({
    name: a.bloggerName || '', xhsAccount: a.xhsAccount || '', xhsUrl: a.xhsUrl || '',
    xhsFollowers: String(a.xhsFollowers || ''), cooperation: a.cooperation || '', platformBio: a.platformBio || '',
    otherPlatforms: (a.otherPlatforms || []).map(p => ({ type: p.type || '', account: p.account || '', followers: String(p.followers || ''), url: p.url || '' })),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const setPlat = (i, k, v) => setF(s => ({ ...s, otherPlatforms: s.otherPlatforms.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const addPlat = () => setF(s => ({ ...s, otherPlatforms: [...s.otherPlatforms, { type: '', account: '', followers: '', url: '' }] }));
  const delPlat = (i) => setF(s => ({ ...s, otherPlatforms: s.otherPlatforms.filter((_, idx) => idx !== i) }));
  const save = async () => {
    if (!a.bloggerProfileId) { onToast && onToast('缺少博主档案，无法保存'); return; }
    setSaving(true);
    try {
      await tandianApi.updateBlogger(a.bloggerProfileId, {
        name: f.name.trim(), xhsAccount: f.xhsAccount.trim(), xhsUrl: f.xhsUrl.trim(),
        xhsFollowers: Number(f.xhsFollowers) || 0, cooperation: f.cooperation.trim(), platformBio: f.platformBio.trim(),
        otherPlatforms: f.otherPlatforms.map(p => ({ type: p.type.trim(), account: p.account.trim(), followers: Number(p.followers) || 0, url: p.url.trim() })),
      });
      await patch(a._id, { bloggerName: f.name.trim(), xhsAccount: f.xhsAccount.trim(), xhsFollowers: Number(f.xhsFollowers) || 0 });
      onSaved && onSaved(); onToast && onToast('已保存'); onClose();
    } finally { setSaving(false); }
  };
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>
        <div style={st.modalHead}><p style={st.modalTitle}>编辑博主资料</p></div>
        <div style={st.modalScroll}>
          <div style={st.editLabel}>姓名</div>
          <input style={st.editInput} value={f.name} onChange={e => set('name', e.target.value)} />
          <div style={st.editLabel}>小红书账号</div>
          <input style={st.editInput} value={f.xhsAccount} onChange={e => set('xhsAccount', e.target.value)} />
          <div style={st.editLabel}>小红书主页链接</div>
          <input style={st.editInput} value={f.xhsUrl} onChange={e => set('xhsUrl', e.target.value)} placeholder="https://..." />
          <div style={st.editLabel}>小红书粉丝数</div>
          <input style={st.editInput} type="number" min="0" value={f.xhsFollowers} onChange={e => set('xhsFollowers', e.target.value.replace(/\D/g, ''))} />
          <div style={st.editLabel}>发帖配合度</div>
          <input style={st.editInput} value={f.cooperation} onChange={e => set('cooperation', e.target.value)} />
          <div style={st.editSection}>其他平台</div>
          {f.otherPlatforms.map((p, i) => (
            <div key={i} style={st.platEdit}>
              <div style={st.platEditRow}>
                <input style={st.platIn} placeholder="平台(如ins)" value={p.type} onChange={e => setPlat(i, 'type', e.target.value)} />
                <input style={st.platIn} placeholder="账号" value={p.account} onChange={e => setPlat(i, 'account', e.target.value)} />
                <input style={st.platInNum} type="number" placeholder="粉丝" value={p.followers} onChange={e => setPlat(i, 'followers', e.target.value.replace(/\D/g, ''))} />
                <button style={st.platDel} onClick={() => delPlat(i)}>×</button>
              </div>
              <input style={st.editInput} placeholder="主页链接" value={p.url} onChange={e => setPlat(i, 'url', e.target.value)} />
            </div>
          ))}
          <button style={{ ...ac.act, marginTop: 4 }} onClick={addPlat}>+ 添加平台</button>
          <div style={st.editLabel}>平台介绍（对外复制用，留空＝用博主简介）</div>
          <textarea style={st.editArea} value={f.platformBio} onChange={e => set('platformBio', e.target.value)} />
        </div>
        <div style={st.modalBtns}>
          <GhostBtn onClick={onClose}>取消</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving}>{saving ? '保存中…' : '保存'}</PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

// 跟商家确认时间：改时间 / 人数 / 备注，再复制文案发微信（时间和备注就这一份）
function ConfirmTimeModal({ apps, patch, onClose, onToast }) {
  const list = [...apps.filter(a => IN_PROGRESS.includes(a.status))].sort(cmp);
  const [edits, setEdits] = useState(() => {
    const m = {};
    apps.forEach(a => {
      m[a._id] = { people: String(confirmPeople(a)), note: a.note || '', visitAt: a.visitAt ? toLocalInput(a.visitAt) : '' };
    });
    return m;
  });
  const setField = (id, k, v) => setEdits(p => ({ ...p, [id]: { ...(p[id] || {}), [k]: v } }));
  // 把编辑中的值套回博主数据，用来生成预览文案
  const merged = (a) => {
    const e = edits[a._id] || {};
    return {
      ...a,
      note: String(e.note || '').trim(),
      visitPeopleCount: Math.max(1, parseInt(e.people, 10) || 1),
      visitAt: e.visitAt ? new Date(e.visitAt) : null,
    };
  };
  const saveOne = (a) => {
    const e = edits[a._id]; if (!e) return;
    const people = Math.max(1, parseInt(e.people, 10) || 1);
    const note = String(e.note || '').trim();
    const visitAt = e.visitAt ? new Date(e.visitAt).toISOString() : null;
    const origVisit = a.visitAt ? new Date(a.visitAt).toISOString() : null;
    if (people !== (a.visitPeopleCount || 1) || note !== (a.note || '') || visitAt !== origVisit) {
      patch(a._id, { visitPeopleCount: people, note, visitAt });
    }
  };
  const fullText = list.map((a, i) => copyLine(merged(a), i)).join('\n\n');
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <div style={st.modalHead}><p style={st.modalTitle}>跟商家确认时间</p></div>
        <div style={st.modalScroll}>
          <p style={st.ctHint}>时间和备注是博主报名时填的，这里可以直接改（改完记得提醒博主）。改好后复制文案发给商家。</p>
          {list.length === 0 ? <p style={st.dim}>还没有已选中的博主。</p> : (
            <>
              <button style={st.ctFullBtn} onClick={() => copyText(fullText).then(() => onToast(`已复制完整文案（${list.length}位）`))}>复制完整文案（{list.length} 位）</button>
              <div style={st.ctList}>
                {list.map(a => (
                  <div key={a._id} style={st.ctCard}>
                    <div style={st.ctName}>{a.bloggerName || '博主'}{needsTimeConfirm(a) ? ' · 待定时间' : ''}</div>
                    <input style={st.ctDtInput} type="datetime-local" value={edits[a._id]?.visitAt || ''}
                      onChange={e => setField(a._id, 'visitAt', e.target.value)} onBlur={() => saveOne(a)} />
                    <div style={st.ctFields}>
                      <input style={st.ctTimeInput} placeholder="备注（店铺 / 套餐等）" value={edits[a._id]?.note || ''}
                        onChange={e => setField(a._id, 'note', e.target.value)} onBlur={() => saveOne(a)} />
                      <input style={st.ctPeopleInput} type="number" min="1" value={edits[a._id]?.people || '1'}
                        onChange={e => setField(a._id, 'people', e.target.value)} onBlur={() => saveOne(a)} />
                      <span style={st.ctPeopleUnit}>人</span>
                    </div>
                    <div style={st.ctPreviewRow}>
                      <span style={st.ctText}>{copyLine(merged(a), null)}</span>
                      <button style={st.ctCopy} onClick={() => copyText(copyLine(merged(a), null)).then(() => onToast('已复制'))}>复制</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div style={st.modalBtns}><GhostBtn onClick={onClose}>关闭</GhostBtn></div>
      </div>
    </div>
  );
}

function StatusButtons({ a, patch, setViewDraft, inModal }) {
  const s = a.status;
  return (
    <div style={ac.wrap}>
      {s === 'applied' && (<>
        <button style={ac.ok} onClick={() => patch(a._id, { status: 'listed' })}>上架给商家</button>
        <button style={ac.no} onClick={() => patch(a._id, { status: 'not_selected' })}>不选</button>
      </>)}
      {s === 'listed' && (<>
        <span style={ac.tip}>已推商家，等待挑选</span>
        <button style={ac.ok} onClick={() => patch(a._id, { status: 'selected' })}>代商家选中</button>
        <button style={ac.no} onClick={() => patch(a._id, { status: 'not_selected' })}>代商家未选</button>
        <button style={ac.act} onClick={() => patch(a._id, { status: 'applied' })}>撤回</button>
      </>)}
      {s === 'selected' && <button style={ac.act} onClick={() => patch(a._id, { status: 'visited' })}>标记已探店</button>}
      {s === 'draft_submitted' && (<>
        {!inModal && <button style={ac.act} onClick={() => setViewDraft(a)}>查看草稿</button>}
        <button style={ac.ok} onClick={() => patch(a._id, { status: 'draft_approved' })}>草稿通过</button>
      </>)}
      {s === 'draft_approved' && <span style={ac.tip}>已通知博主发布，等待发帖回填</span>}
      {s === 'published' && (<>
        {a.xhsPostUrl && <a style={ac.act} href={normalizeUrl(a.xhsPostUrl)} target="_blank" rel="noreferrer">看发帖</a>}
        {!inModal && <button style={ac.act} onClick={() => setViewDraft(a)}>查看内容</button>}
        <button style={ac.ok} onClick={() => patch(a._id, { status: 'done' })}>标记完成</button>
      </>)}
      {s === 'done' && a.xhsPostUrl && <a style={ac.act} href={normalizeUrl(a.xhsPostUrl)} target="_blank" rel="noreferrer">看发帖</a>}
      {(s === 'not_selected' || s === 'cancelled') && <button style={ac.act} onClick={() => patch(a._id, { status: 'applied' })}>恢复报名</button>}
      {IN_PROGRESS.includes(s) && <button style={ac.cancel} onClick={() => patch(a._id, { status: 'cancelled' })}>取消·退名额</button>}
    </div>
  );
}

function Actions({ a, patch, setViewDraft }) {
  return (
    <div>
      <StatusButtons a={a} patch={patch} setViewDraft={setViewDraft} />
      {NOTE_EDITABLE(a.status) && <NoteTime a={a} patch={patch} />}
    </div>
  );
}

// 时间和备注只有这一份（博主报名时填，运营在这里改）
function NoteTime({ a, patch }) {
  const [note, setNote] = useState(a.note || '');
  const [visitAt, setVisitAt] = useState(a.visitAt ? toLocalInput(a.visitAt) : '');
  const [saving, setSaving] = useState(false);
  const origVisit = a.visitAt ? toLocalInput(a.visitAt) : '';
  const dirty = note !== (a.note || '') || visitAt !== origVisit;
  const save = async () => {
    setSaving(true);
    try { await patch(a._id, { note, visitAt: visitAt ? new Date(visitAt).toISOString() : null }); }
    finally { setSaving(false); }
  };
  return (
    <div style={ac.noteBox}>
      <div style={ac.noteRow}>
        <span style={ac.noteLabel}>探店时间</span>
        <input type="datetime-local" style={ac.dt} value={visitAt} onChange={(e) => setVisitAt(e.target.value)} />
      </div>
      <textarea style={ac.note} placeholder="备注（博主按报名要求填的店铺 / 套餐等，运营可改）" value={note} onChange={(e) => setNote(e.target.value)} />
      {dirty && <div style={ac.timeTip}>{TIME_TIP}</div>}
      {dirty && <button style={ac.saveNote} onClick={save} disabled={saving}>{saving ? '保存中…' : '保存时间 / 备注'}</button>}
    </div>
  );
}

function EditActivityModal({ activity, onSaved, onClose }) {
  const [name, setName] = useState(activity.name || '');
  const [intro, setIntro] = useState(activity.intro || '');
  const [neededCount, setNeededCount] = useState(String(activity.neededCount || ''));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const save = async () => {
    if (!name.trim()) { setMsg('请填写活动名称'); return; }
    setSaving(true); setMsg('');
    try {
      await tandianApi.updateActivity(activity._id, { name: name.trim(), intro: intro.trim(), neededCount: Number(neededCount) || 0 });
      onSaved();
    } catch (e) { setMsg(e.response?.data?.error || '保存失败'); setSaving(false); }
  };
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>
        <p style={st.modalTitle}>编辑活动</p>
        <div style={st.modalScroll}>
          <div style={st.editLabel}>活动名称</div>
          <input style={st.editInput} value={name} onChange={(e) => setName(e.target.value)} placeholder="活动名称" />
          <div style={st.editLabel}>招募说明（给博主看）</div>
          <textarea style={st.editArea} value={intro} onChange={(e) => setIntro(e.target.value)} placeholder="商家要求、可到店时间、招募人群等" />
          <div style={st.editLabel}>计划招募人数</div>
          <input style={st.editInput} type="tel" inputMode="numeric" value={neededCount} onChange={(e) => setNeededCount(e.target.value.replace(/\D/g, ''))} placeholder="如 3" />
          {msg && <p style={st.editErr}>{msg}</p>}
          <p style={st.editHint}>活动内容随时可改（含已发给商家、已有博主报名后）；改完博主端「可报名」看到的就是新内容。</p>
        </div>
        <div style={st.modalBtns}>
          <GhostBtn onClick={onClose}>取消</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving}>{saving ? '保存中…' : '保存'}</PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

function DraftModal({ app, patch, onClose, onToast }) {
  const visitReq = app.visitTimeRequested || '';   // 旧数据的文字时间，只读展示
  const [people, setPeople] = useState(String(app.visitPeopleCount || 1));
  const [visitAt, setVisitAt] = useState(app.visitAt ? toLocalInput(app.visitAt) : '');
  const [note, setNote] = useState(app.note || '');
  const [title, setTitle] = useState(app.draft?.title || '');
  const [content, setContent] = useState(app.draft?.content || '');
  const [images, setImages] = useState(app.draft?.images || []);
  const [saving, setSaving] = useState(false);
  const lb = useLightbox();
  const hasDraft = !!(app.draft?.title || app.draft?.content || (app.draft?.images || []).length);
  const removeImg = (i) => setImages((imgs) => imgs.filter((_, idx) => idx !== i));
  const save = async () => {
    setSaving(true);
    try {
      const body = {
        visitPeopleCount: Math.max(1, parseInt(people, 10) || 1),
        visitAt: visitAt ? new Date(visitAt).toISOString() : null,
        note,
      };
      if (hasDraft) body.draft = { title: title.trim(), content, images };
      await patch(app._id, body);
      onClose();
    } finally { setSaving(false); }
  };
  // 状态动作：改完直接关弹窗，回列表看最新
  const actClose = (id, body) => { onClose(); return patch(id, body); };
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>
        <div style={st.modalHead}>
          <p style={st.modalTitle}>{app.bloggerName || '博主'} · 报名信息</p>
        </div>
        <div style={st.modalScroll}>
          <div style={st.editSection}>探店安排（博主报名时填，运营可改）</div>
          <div style={st.editLabel}>探店时间</div>
          <input style={st.editInput} type="datetime-local" value={visitAt} onChange={(e) => setVisitAt(e.target.value)} />
          <div style={st.timeTip}>{TIME_TIP}</div>
          <div style={st.editLabel}>到店人数</div>
          <input style={st.editInput} type="number" min="1" value={people} onChange={(e) => setPeople(e.target.value)} placeholder="到店人数" />
          <div style={st.editLabel}>备注（店铺 / 套餐等）</div>
          <textarea style={st.editArea} value={note} onChange={(e) => setNote(e.target.value)} placeholder="备注" />
          {visitReq && <p style={st.legacyReq}>旧数据·博主当时填的时间：{visitReq}</p>}

          {hasDraft && (<>
            <div style={st.editSection}>博主草稿（发给商家前可删改，不影响已发小红书）</div>
            <div style={st.editLabel}>标题</div>
            <input style={st.editInput} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="标题（选填）" />
            <div style={st.editLabel}>正文</div>
            <textarea style={st.editArea} value={content} onChange={(e) => setContent(e.target.value)} placeholder="正文" />
            {images.length > 0 && (
              <div style={st.imgGrid}>
                {images.map((u, i) => (
                  <div key={i} style={st.imgCell}>
                    <img src={u} alt="" style={st.img} onClick={() => lb.open(images, i)} />
                    <span style={st.imgDel} onClick={() => removeImg(i)}>×</span>
                  </div>
                ))}
              </div>
            )}
            {app.xhsPostUrl && <p style={st.link}>发帖链接：<a href={normalizeUrl(app.xhsPostUrl)} target="_blank" rel="noreferrer">{app.xhsPostUrl}</a></p>}
            {(app.proofImages || []).length > 0 && (<><p style={st.proofLabel}>发帖证据</p><div style={st.imgGrid}>{app.proofImages.map((u, i) => <img key={i} src={u} alt="" style={st.img} onClick={() => lb.open(app.proofImages, i)} />)}</div></>)}
          </>)}

          <div style={st.editSection}>操作</div>
          <StatusButtons a={app} patch={actClose} setViewDraft={() => {}} inModal />
          <div style={st.wrapRow}>
            <button style={ac.act} onClick={() => copyText(copyLine({ ...app, note, visitAt: visitAt ? new Date(visitAt) : app.visitAt, visitPeopleCount: Math.max(1, parseInt(people, 10) || 1) }, null)).then(() => onToast && onToast('已复制文案'))}>复制文案</button>
          </div>
        </div>
        <div style={st.modalBtns}>
          <GhostBtn onClick={onClose}>取消</GhostBtn>
          <PrimaryBtn onClick={save} disabled={saving}>{saving ? '保存中…' : '保存'}</PrimaryBtn>
        </div>
        {lb.node}
      </div>
    </div>
  );
}

function AddBloggerModal({ list, existing, onPick, onClose }) {
  const [q, setQ] = useState('');
  const existSet = new Set(existing || []);
  const filtered = (list || []).filter(b => !existSet.has(b.phone) &&
    (!q.trim() || (b.name || '').includes(q) || (b.xhsAccount || '').includes(q) || (b.phone || '').includes(q)));
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>
        <p style={st.modalTitle}>补博主（已通过审核的）</p>
        <input style={st.search} placeholder="搜索姓名 / 小红书 / 手机" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={st.modalScroll}>
          {list === null ? <p style={st.dim}>加载中…</p>
            : filtered.length === 0 ? <p style={st.dim}>没有可添加的博主</p>
            : filtered.map(b => (
              <div key={b._id} style={st.pickRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={st.pickName}>{b.name || '（未填姓名）'}</div>
                  <div style={st.pickSub}>{b.xhsAccount || '—'} · {b.xhsFollowers || 0}粉</div>
                </div>
                <button style={ac.ok} onClick={() => onPick(b._id)}>添加</button>
              </div>
            ))}
        </div>
        <div style={st.modalBtns}><GhostBtn onClick={onClose}>关闭</GhostBtn></div>
      </div>
    </div>
  );
}

const st = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  body: { flex: 1, padding: '12px 12px 24px' },
  dim: { fontSize: 13, color: '#9ca3af', lineHeight: 1.6 },
  tip: { fontSize: 12, color: '#9ca3af', lineHeight: 1.6, margin: '0 2px 10px' },
  overview: { background: '#fff', borderRadius: 12, padding: '14px 16px', marginBottom: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  ovLine: { display: 'flex', gap: 10, fontSize: 14, color: '#111', padding: '4px 0' },
  ovLabel: { width: 40, color: '#9ca3af', flexShrink: 0 },
  ovIntro: { fontSize: 13, color: '#555', lineHeight: 1.6, background: '#fafafa', borderRadius: 8, padding: '8px 12px', marginTop: 8, whiteSpace: 'pre-wrap' },
  mFlow: { background: '#f5f3ff', border: '1px solid #e9e2fb', borderRadius: 12, padding: '12px 14px', marginBottom: 12 },
  mFlowTitle: { fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 10 },
  mFlowBtns: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 },
  mFlowHint: { fontSize: 12, color: '#9b8bc4', lineHeight: 1.6, marginTop: 10 },
  mStepBtn: { padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  mStepOn: { background: '#7C3AED', color: '#fff', border: '1px solid #7C3AED' },
  mStepOff: { background: '#fff', color: '#7C3AED', border: '1px solid #d9ccf7' },
  inviteBtn: { padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', background: '#fff7f8', color: '#FA1C51', border: '1px solid #ffd6de' },
  // 只读进度条
  stepper: { display: 'flex', alignItems: 'center', marginBottom: 8 },
  stepPill: { fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 14, whiteSpace: 'nowrap' },
  stepCur: { background: '#FA1C51', color: '#fff' },
  stepDone: { background: '#ffe3ea', color: '#FA1C51' },
  stepTodo: { background: '#f3f4f6', color: '#9ca3af' },
  stepLine: { flex: 1, height: 2, background: '#e5e7eb', margin: '0 4px' },
  stepLineOn: { background: '#f7a8bd' },
  stepHint: { fontSize: 12, color: '#6b7280', lineHeight: 1.6, marginBottom: 14 },
  stepHintNow: { color: '#FA1C51' },
  stageRow: { display: 'flex', gap: 6, marginBottom: 14 },
  stageBtn: { flex: 1, padding: '8px 0', background: '#fff', color: '#6b7280', border: '1px solid #eee', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  stageOn: { background: '#FA1C51', color: '#fff', borderColor: '#FA1C51', fontWeight: 600 },
  ovBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: '#fff', borderRadius: 10, padding: '8px 12px', marginBottom: 8, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  ovBarMain: { fontSize: 14, fontWeight: 700, color: '#111' },
  ovBarSub: { fontSize: 12, color: '#6b7280', flex: 1 },
  ovToggle: { background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, padding: '4px 10px', fontSize: 12, color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' },
  guideBox: { background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 10, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  sectionTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 },
  chipWrap: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', flex: 1 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#111' },
  selAllBtn: { padding: '5px 12px', background: '#f5f3ff', color: '#7C3AED', border: '1px solid #d9ccf7', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  filterRow: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 },
  filterChip: { padding: '5px 12px', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  filterChipOn: { background: '#FA1C51', color: '#fff', borderColor: '#FA1C51' },
  filterChipDim: { opacity: 0.4, cursor: 'default' },
  rowList: { display: 'flex', flexDirection: 'column', gap: 6 },
  selBar: { position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'center', background: '#fff', borderRadius: 12, padding: '10px 14px', boxShadow: '0 4px 20px rgba(0,0,0,0.18)', zIndex: 90, maxWidth: '94vw' },
  selCount: { fontSize: 13, fontWeight: 700, color: '#111' },
  selCopy: { padding: '7px 14px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  selOk: { padding: '7px 14px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  selNo: { padding: '7px 12px', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  selClear: { padding: '7px 10px', background: 'none', color: '#9ca3af', border: 'none', fontSize: 13, cursor: 'pointer' },
  toast: { position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 80, background: 'rgba(17,17,17,0.9)', color: '#fff', fontSize: 13, padding: '9px 16px', borderRadius: 20, zIndex: 120 },
  ctHint: { fontSize: 12, color: '#6b7280', lineHeight: 1.7, margin: '0 0 12px' },
  ctFullBtn: { width: '100%', padding: '11px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 12 },
  ctList: { display: 'flex', flexDirection: 'column', gap: 8 },
  ctRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #eee', borderRadius: 8 },
  ctText: { flex: 1, fontSize: 13, color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
  ctCopy: { flexShrink: 0, padding: '6px 14px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  ctCard: { border: '1px solid #eee', borderRadius: 8, padding: '10px' },
  ctName: { fontSize: 14, fontWeight: 700, color: '#111', marginBottom: 4 },
  ctRef: { fontSize: 12, color: '#9ca3af', marginBottom: 8, lineHeight: 1.5 },
  ctFields: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  ctNoteInput: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none', marginBottom: 8 },
  ctTimeInput: { flex: 1, minWidth: 0, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' },
  ctPeopleInput: { width: 52, padding: '8px 8px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none', textAlign: 'center' },
  ctPeopleUnit: { fontSize: 13, color: '#6b7280' },
  ctPreviewRow: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  ctDtInput: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none', marginBottom: 8 },
  bcTop: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 },
  bcAvatar: { width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  bcAvatarFallback: { width: 56, height: 56, borderRadius: '50%', background: '#FA1C51', color: '#fff', fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  bcName: { fontSize: 17, fontWeight: 700, color: '#111' },
  bcFans: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  bcBio: { fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', background: '#fafafa', borderRadius: 8, padding: 12, marginBottom: 12 },
  bcLink: { display: 'inline-block', fontSize: 14, color: '#FA1C51', fontWeight: 700, textDecoration: 'none' },
  bcPlats: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 },
  bcPlatRow: { fontSize: 13, color: '#374151' },
  bcPlatLink: { marginLeft: 6, color: '#FA1C51', textDecoration: 'none', fontWeight: 600 },
  platEdit: { border: '1px solid #eee', borderRadius: 8, padding: 8, marginBottom: 8 },
  platEditRow: { display: 'flex', gap: 6, marginBottom: 6 },
  platIn: { flex: 1, minWidth: 0, padding: '8px 8px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' },
  platInNum: { width: 64, padding: '8px 8px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' },
  platDel: { flexShrink: 0, width: 30, background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 7, fontSize: 16, cursor: 'pointer' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  appCard: { background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 6px rgba(0,0,0,0.05)' },
  appTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  appName: { fontSize: 15, fontWeight: 700, color: '#111' },
  appXhs: { fontSize: 13, fontWeight: 400, color: '#6b7280' },
  appMeta: { fontSize: 12, color: '#9ca3af', marginTop: 4 },
  reqRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 6 },
  reqTime: { fontSize: 13, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '2px 8px' },
  miniEdit: { flexShrink: 0, background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 12px', fontSize: 12, color: '#374151', cursor: 'pointer' },
  contactLine: { fontSize: 13, color: '#374151', marginTop: 6, fontWeight: 600 },
  noteLine: { fontSize: 13, color: '#374151', marginTop: 6, lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  legacyReq: { fontSize: 12, color: '#9ca3af', lineHeight: 1.6, margin: '8px 0 0' },
  timeTip: { fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, padding: '7px 9px', lineHeight: 1.6, margin: '8px 0 0' },
  contactTel: { color: '#FA1C51', textDecoration: 'none', fontWeight: 700 },
  mComment: { fontSize: 12, color: '#7C3AED', background: '#f5f3ff', borderRadius: 6, padding: '6px 8px', marginTop: 6, lineHeight: 1.5 },
  badge: { fontSize: 11, padding: '2px 8px', borderRadius: 5, fontWeight: 600 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modal: { background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: 380, maxHeight: '82vh', display: 'flex', flexDirection: 'column' },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 12px' },
  modalTitle: { fontSize: 15, fontWeight: 700, color: '#111', margin: 0 },
  editBtn: { background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 12px', fontSize: 13, color: '#374151', cursor: 'pointer' },
  modalScroll: { overflowY: 'auto', flex: 1, marginBottom: 14 },
  modalBtns: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  editSection: { fontSize: 13, fontWeight: 700, color: '#FA1C51', margin: '16px 0 4px', paddingBottom: 4, borderBottom: '1px solid #f0f0f0' },
  wrapRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  editLabel: { fontSize: 12, color: '#6b7280', fontWeight: 600, margin: '10px 0 6px' },
  editInput: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' },
  editArea: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, lineHeight: 1.7, minHeight: 140, resize: 'vertical', outline: 'none' },
  imgCell: { position: 'relative', width: 88, height: 88 },
  imgDel: { position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 16, lineHeight: '22px', textAlign: 'center', cursor: 'pointer' },
  editHint: { fontSize: 12, color: '#9ca3af', lineHeight: 1.6, margin: '12px 0 0' },
  editErr: { fontSize: 13, color: '#FA1C51', margin: '10px 0 0' },
  search: { width: '100%', padding: '10px 12px', border: '1px solid #eee', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 12 },
  draftTitle: { fontSize: 15, fontWeight: 700, color: '#111', margin: '0 0 8px' },
  draftContent: { fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: '0 0 10px' },
  imgGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 },
  img: { width: 88, height: 88, objectFit: 'cover', borderRadius: 8 },
  link: { fontSize: 13, color: '#374151', wordBreak: 'break-all', margin: '0 0 10px' },
  proofLabel: { fontSize: 12, color: '#6b7280', fontWeight: 600, margin: '0 0 6px' },
  pickRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f5f5f5' },
  pickName: { fontSize: 14, fontWeight: 600, color: '#111' },
  pickSub: { fontSize: 12, color: '#9ca3af', marginTop: 2 },
};

const ac = {
  wrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, alignItems: 'center' },
  ok: { padding: '6px 14px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  no: { padding: '6px 12px', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', fontSize: 13 },
  act: { padding: '6px 12px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', fontSize: 13, textDecoration: 'none' },
  cancel: { padding: '6px 12px', background: '#fff', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 7, cursor: 'pointer', fontSize: 13 },
  tip: { fontSize: 12, color: '#9ca3af' },
  noteBox: { marginTop: 10, paddingTop: 10, borderTop: '1px dashed #eee' },
  noteRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  noteLabel: { fontSize: 12, color: '#6b7280', flexShrink: 0 },
  dt: { flex: 1, padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, outline: 'none' },
  note: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 13, minHeight: 48, resize: 'vertical', outline: 'none' },
  timeTip: { fontSize: 12, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, padding: '7px 9px', lineHeight: 1.6, marginTop: 8 },
  saveNote: { marginTop: 8, padding: '6px 14px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
};

const rw = {
  card: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  cardSel: { boxShadow: '0 0 0 2px #7C3AED' },
  main: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' },
  cb: { width: 17, height: 17, flexShrink: 0 },
  info: { flex: 1, minWidth: 0, cursor: 'pointer' },
  line1: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  name: { fontSize: 14, fontWeight: 700, color: '#111', textDecoration: 'underline', textDecorationColor: '#e5e7eb', textUnderlineOffset: 2, cursor: 'pointer' },
  xhs: { fontSize: 12, color: '#6b7280' },
  badge: { fontSize: 11, padding: '1px 7px', borderRadius: 5, fontWeight: 600 },
  line2: { fontSize: 12, color: '#9ca3af', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  copyBtn: { flexShrink: 0, padding: '5px 10px', background: '#f5f3ff', color: '#7C3AED', border: '1px solid #d9ccf7', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  copyBtnWarn: { background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' },
  editBtn: { flexShrink: 0, padding: '5px 10px', background: '#fff7f8', color: '#FA1C51', border: '1px solid #ffd6de', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  expand: { flexShrink: 0, padding: '5px 10px', background: 'none', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12, color: '#6b7280', cursor: 'pointer' },
  detail: { padding: '0 12px 12px', borderTop: '1px solid #f5f5f5' },
};
